import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroAccent } from '@/components/shared/PageHero';
import UnderpaidChecker from '@/components/shared/UnderpaidChecker';
import { HomePageOccupationMap } from '@/components/shared/HomePageOccupationMap';
import { generateFAQPageSchema, generateMultipleSchemas, generateWebPageSchema } from '@/lib/schema';

const AmIUnderpaid = () => {
  const navigate = useNavigate();
  const [selectedOccupation, setSelectedOccupation] = useState<{ code: string; title: string }>({
    code: '15-1252',
    title: 'Software Developers',
  });

  const handleOccupationChange = (occupation: { code: string; title: string }) => {
    setSelectedOccupation(occupation);
  };

  const faqItems = [
    {
      question: 'How does the "Am I underpaid?" check work?',
      answer: 'We compare your salary to the official Bureau of Labor Statistics wage distribution for your occupation and location, then estimate what percentile you fall in. Below the median means more than half of workers in your field earn more than you.',
    },
    {
      question: 'Is the salary checker free?',
      answer: 'Yes, completely free. You can optionally download a polished PDF report to use in a negotiation or job application.',
    },
    {
      question: 'Does AI affect what I should be paid?',
      answer: 'Increasingly, yes. PwC\u2019s AI Jobs Barometer found workers with AI skills earn roughly 62% more than peers in the same role without them, and BLS projects AI-driven occupations like data scientists to grow 33.5% through 2034. Your results include an AI outlook for your occupation, and our AI Economy Jobs hub shows salaries for the careers AI is creating.',
    },
  ];
  const schemas = generateMultipleSchemas([
    generateWebPageSchema('Am I Underpaid? Free Salary Checker', 'Compare your salary to official BLS data and see your percentile instantly.', 'https://wageatlas.com/am-i-underpaid', {}),
    generateFAQPageSchema(faqItems),
  ]);

  return (
    <>
      <SeoHead
        title="Am I Underpaid? Free Salary Checker | Wage Atlas"
        description="Find out instantly if you're underpaid. Compare your salary to official BLS wage data for your job and state, and see exactly what percentile you fall in - free."
        canonical="https://wageatlas.com/am-i-underpaid/"
        schemas={schemas}
      />

      <div className="min-h-screen bg-background">
        <PageHero
          title={
            <>
              Are you being
              <HeroAccent> underpaid?</HeroAccent>
            </>
          }
          titleSize="large"
          subtitle="In a tight job market, guessing is expensive. Enter your job and pay to see your percentile against official BLS data - in seconds, free."
          maxWidth="max-w-2xl"
        >
          <div className="w-full max-w-xl mx-auto min-w-0">
            <UnderpaidChecker variant="compact" onOccupationSelect={handleOccupationChange} />
            <p className="text-sm text-white/80 mt-4">
              No signup. No paywall. Official BLS &amp; BEA data.
            </p>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => navigate('/')}
              className="mt-5 h-auto min-h-11 w-full max-w-full min-w-0 whitespace-normal px-4 text-center sm:w-auto sm:px-8 border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
            >
              <Search className="shrink-0" />
              <span className="text-balance">
                Exploring careers? See what any job really pays
              </span>
              <ArrowRight className="shrink-0" />
            </Button>
          </div>
        </PageHero>

        <section id="geographic-analysis" className="py-12 px-2 sm:px-4 bg-muted/20">
          <div className="mx-auto w-full max-w-6xl">
            <HomePageOccupationMap
              selectedOccupation={selectedOccupation}
              onOccupationChange={handleOccupationChange}
            />
          </div>
        </section>
      </div>
    </>
  );
};

export default AmIUnderpaid;
